import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const capabilities = [
  {
    title: 'Commodity Trading',
    description: 'Strategic sourcing and trading of industrial commodities including steel, chemicals, construction materials, and agricultural products between China and East African markets.',
    icon: 'ri-swap-box-line',
  },
  {
    title: 'Industrial Equipment',
    description: 'Supply of heavy machinery, manufacturing equipment, and industrial tools from China\'s leading manufacturers to businesses across East Africa.',
    icon: 'ri-settings-3-line',
  },
  {
    title: 'Raw Materials',
    description: 'Procurement and supply of essential raw materials including metals, minerals, textiles, and petrochemical derivatives for African industries.',
    icon: 'ri-stack-line',
  },
  {
    title: 'Consumer Goods',
    description: 'Bulk trading of consumer products — electronics, household goods, textiles, and lifestyle products — connecting Chinese manufacturers with African distributors.',
    icon: 'ri-shopping-bag-2-line',
  },
  {
    title: 'Market Intelligence',
    description: 'Real-time insights into market trends, pricing dynamics, and demand patterns across both Chinese and East African markets to optimize trading decisions.',
    icon: 'ri-line-chart-line',
  },
  {
    title: 'Trade Finance Support',
    description: 'Facilitation of trade finance solutions including letters of credit, payment guarantees, and structured trade finance to enable smooth cross-border transactions.',
    icon: 'ri-bank-line',
  },
];

const process = [
  { step: '01', title: 'Market Analysis', description: 'We analyze demand trends, price dynamics, and supply chain factors to identify the best trading opportunities for your business.' },
  { step: '02', title: 'Supplier Matching', description: 'Leverage our extensive network of verified Chinese manufacturers to find the optimal trading partners for your specific requirements.' },
  { step: '03', title: 'Price Negotiation', description: 'Our team negotiates competitive pricing and favorable terms, leveraging volume relationships and deep market knowledge.' },
  { step: '04', title: 'Quality Assurance', description: 'Rigorous quality inspections and compliance verification before any shipment leaves the factory.' },
  { step: '05', title: 'Logistics Coordination', description: 'Seamless freight management from factory to destination, including customs clearance and documentation.' },
  { step: '06', title: 'After-Sales Support', description: 'Ongoing support, warranty management, and relationship maintenance to ensure long-term trading success.' },
];

export default function TradingPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">

        {/* Hero */}
        <section className="relative w-full bg-primary-900 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <img
              src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20busy%20international%20trading%20port%20with%20massive%20container%20ships%20being%20loaded%20at%20golden%20hour%2C%20global%20commerce%20and%20trade%20concept%2C%20cinematic%20wide%20angle%2C%20warm%20atmospheric%20lighting%2C%20professional%20photography&width=1920&height=800&seq=trading-hero&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-950/90 via-primary-950/80 to-primary-950/95"></div>
          </div>
          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-16 md:py-24">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-400 mb-4">Services</span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-5 leading-tight">
              Trading
            </h1>
            <p className="text-white/50 max-w-2xl text-sm md:text-base leading-relaxed mb-8">
              Global commodity and product trading connecting East African markets with premium Chinese manufactured goods, industrial equipment, and raw materials — backed by deep market intelligence and verified supplier relationships.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="px-7 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/25">
                Start Trading
              </Link>
              <Link to="/products" className="px-7 py-3.5 bg-white/10 backdrop-blur-sm text-white font-semibold text-sm rounded-full hover:bg-white/20 border border-white/30 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Browse Products
              </Link>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Overview</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">Why Trade Through BEKAALI?</h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                International trade between China and East Africa presents immense opportunities — and significant complexity. Our trading division simplifies the entire process, giving you access to China&apos;s vast manufacturing ecosystem without the risks, language barriers, and logistical challenges of going it alone.
              </p>
            </div>

            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                China is the world&apos;s largest manufacturing economy and East Africa&apos;s biggest trading partner. However, navigating the Chinese manufacturing landscape requires local knowledge, cultural understanding, robust supplier verification, and sophisticated logistics management. BEKAALI&apos;s trading division provides all of this — and more.
              </p>
              <p>
                Our on-the-ground presence in both Nairobi and Guangzhou means we can physically inspect factories, verify quality standards, negotiate pricing in real-time, and manage the entire procurement-to-delivery process. Whether you need construction machinery, industrial equipment, raw materials, or consumer goods, we connect you with the right suppliers at the right price.
              </p>
              <p>
                We do not simply match buyers with sellers — we manage the complete trading relationship. From initial market research and supplier identification through to quality control, shipping, customs clearance, and after-sales support, BEKAALI is your single point of accountability for successful cross-border trade.
              </p>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Capabilities</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">What We Trade</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {capabilities.map((cap, index) => (
                <div key={index} className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-7 hover:border-accent-200/50 transition-all duration-400">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-white transition-all duration-400">
                    <i className={`${cap.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{cap.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{cap.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-primary-900 overflow-hidden relative">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-[120px]"></div>
          </div>
          <div className="relative z-10 max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-400 mb-4">Process</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4">Our Trading Methodology</h2>
              <p className="text-white/50 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                A structured, proven approach that ensures quality, reliability, and value.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
              {process.map((item, idx) => (
                <div key={idx} className={`group relative p-8 border border-white/5 hover:bg-white/[0.03] transition-all duration-400 ${idx < 3 ? 'lg:border-b' : ''} ${idx % 3 !== 2 ? 'lg:border-r' : ''}`}>
                  <div className="text-5xl font-heading font-bold text-white/5 mb-6 group-hover:text-accent-500/10 transition-colors duration-400">{item.step}</div>
                  <div className="relative -mt-10">
                    <h3 className="text-lg font-heading font-semibold text-white mb-2 group-hover:text-accent-400 transition-colors duration-300">{item.title}</h3>
                    <p className="text-sm text-white/40 group-hover:text-white/60 transition-colors duration-300 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">Ready to Trade?</h2>
            <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base leading-relaxed mb-8">
              Let&apos;s discuss your trading requirements. Our team will identify the best suppliers, negotiate optimal pricing, and manage the entire process.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/25">
              Get in Touch <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}